import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { base44 } from '@/api/base44Client';
import { useAuth } from '@/lib/AuthContext';
import { useToast } from '@/components/ui/use-toast';
import { Send, Mail, Check, X, Loader2, Clock, ArrowRight } from 'lucide-react';

const STORAGE_KEY = 'mr_email_test_recipient';

const EMAIL_GROUPS = [
  {
    group: 'Transactional',
    items: [
      { key: 'order_confirmation', label: 'Order Confirmation + Invoice', desc: 'Sent when a customer pays — branded summary + VAT invoice download.' },
      { key: 'booking_confirmation', label: 'Booking Confirmation', desc: 'Sent when a sim racing session is booked and confirmed.' },
      { key: 'credit_note', label: 'Credit Note', desc: 'Sent when a refund is issued — credit note summary + download.' },
      { key: 'order_status', label: 'Order Status Update', desc: 'Dispatched status email with tracking number.' },
      { key: 'follow_up', label: 'Follow-Up Email', desc: 'Manual follow-up sent to a customer about their order.' },
      { key: 'disclaimer_copy', label: 'Signed Disclaimer Copy', desc: 'Sent to the customer after they sign the sim rig disclaimer — includes the branded one-page PDF for their records.' },
    ],
  },
  {
    group: 'Reviews & Feedback',
    items: [
      { key: 'review_request', label: 'Review Request', desc: 'Asks a product customer for a Google / Trustpilot review.' },
      { key: 'tripadvisor_request', label: 'TripAdvisor Review Request', desc: 'Sent the day after a completed session asking for a TripAdvisor review.' },
      { key: 'experience_feedback', label: 'Experience Feedback', desc: 'Post-session survey invite with a free-session prize draw.' },
    ],
  },
  {
    group: 'Outreach',
    items: [
      { key: 'b2b_invite', label: 'B2B Portal Invite', desc: 'Invites a trade customer to set up their online ordering account.' },
      { key: 'competition_winner', label: 'Competition Winner', desc: 'Notification sent to a monthly competition winner.' },
      { key: 'newsletter_welcome', label: 'Newsletter Welcome', desc: 'Welcome email sent to a new newsletter subscriber.' },
    ],
  },
];

const ALL_KEYS = EMAIL_GROUPS.flatMap((g) => g.items.map((i) => i.key));

const statusStyles = {
  idle: 'text-white/30 border-white/10 bg-white/5',
  sending: 'text-yellow-400 border-yellow-400/30 bg-yellow-400/10',
  sent: 'text-green-400 border-green-400/30 bg-green-400/10',
  failed: 'text-red-400 border-red-400/30 bg-red-400/10',
};

export default function AdminEmailTesting() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [recipient, setRecipient] = useState('');
  const [statuses, setStatuses] = useState({});
  const [sendingAll, setSendingAll] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) setRecipient(saved);
  }, []);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, recipient);
  }, [recipient]);

  const sendOne = async (key) => {
    if (!recipient || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(recipient)) {
      toast({ title: 'Enter a valid recipient email', variant: 'destructive' });
      return null;
    }
    setStatuses((s) => ({ ...s, [key]: { state: 'sending' } }));
    try {
      const res = await base44.functions.invoke('sendTestEmail', { email_type: key, to: recipient });
      const data = res?.data || res;
      if (data?.success) {
        setStatuses((s) => ({ ...s, [key]: { state: 'sent', at: new Date(), subject: data.subject } }));
        return true;
      }
      throw new Error(data?.error || 'Send failed');
    } catch (e) {
      setStatuses((s) => ({ ...s, [key]: { state: 'failed', error: e?.message || 'Failed' } }));
      return false;
    }
  };

  const sendAll = async () => {
    if (!recipient || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(recipient)) {
      toast({ title: 'Enter a valid recipient email', variant: 'destructive' });
      return;
    }
    setSendingAll(true);
    let ok = 0;
    let fail = 0;
    for (const key of ALL_KEYS) {
      const result = await sendOne(key);
      if (result) ok++;
      else fail++;
    }
    setSendingAll(false);
    toast({
      title: `Sent ${ok} of ${ALL_KEYS.length} emails`,
      description: fail ? `${fail} failed — check the rows below.` : 'All delivered — check your inbox.',
      variant: fail ? 'destructive' : 'default',
    });
  };

  if (user?.role !== 'admin') {
    return (
      <div className="p-10 text-center text-white/40">
        Admin access required.
      </div>
    );
  }

  const sentCount = Object.values(statuses).filter((s) => s?.state === 'sent').length;
  const failedCount = Object.values(statuses).filter((s) => s?.state === 'failed').length;

  return (
    <div className="min-h-screen bg-[#0d0d0f] pb-20 pt-16">
      {/* Sticky header */}
      <div className="sticky top-16 z-20 bg-[#0d0d0f]/95 backdrop-blur border-b border-white/5">
        <div className="px-6 lg:px-10 py-5">
          <div className="flex flex-col lg:flex-row lg:items-center gap-4">
            <div className="flex-1">
              <p className="text-primary text-[10px] font-bold tracking-widest mb-1">EMAIL TEST CENTER</p>
              <h1 className="text-white font-black text-2xl uppercase leading-tight">Customer Email Testing</h1>
              <p className="text-white/40 text-xs mt-1">
                Send a real sample of every customer email to your inbox to verify content and deliverability.
                No orders, bookings or records are created.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 lg:items-center">
              <div className="flex items-center gap-2 bg-white/5 border border-white/10 px-3 h-11">
                <Mail className="w-4 h-4 text-white/30" />
                <input
                  type="email"
                  value={recipient}
                  onChange={(e) => setRecipient(e.target.value)}
                  placeholder="recipient@email.com"
                  className="bg-transparent text-white text-sm outline-none w-full sm:w-56 placeholder:text-white/30"
                />
              </div>
              <button
                onClick={sendAll}
                disabled={sendingAll}
                className="bg-primary text-white font-bold text-xs tracking-widest px-5 h-11 flex items-center justify-center gap-2 hover:bg-primary/90 transition-colors disabled:opacity-50"
              >
                {sendingAll ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {sendingAll ? 'SENDING…' : 'SEND ALL'}
              </button>
            </div>
          </div>
          {(sentCount > 0 || failedCount > 0) && (
            <div className="flex items-center gap-4 mt-3 text-[10px] font-bold tracking-widest">
              <span className="text-green-400">{sentCount} SENT</span>
              <span className="text-red-400">{failedCount} FAILED</span>
              <span className="text-white/30">{ALL_KEYS.length - sentCount - failedCount} PENDING</span>
            </div>
          )}
        </div>
      </div>

      {/* Email groups */}
      <div className="px-6 lg:px-10 py-8 max-w-5xl mx-auto space-y-10">
        {EMAIL_GROUPS.map((grp) => (
          <div key={grp.group}>
            <p className="text-white/40 text-[10px] font-bold tracking-widest mb-3">{grp.group.toUpperCase()}</p>
            <div className="grid grid-cols-1 gap-px bg-white/5 border border-white/5">
              {grp.items.map((item) => {
                const st = statuses[item.key] || { state: 'idle' };
                return (
                  <div key={item.key} className="bg-[#0d0d0f] p-4 flex items-center gap-4">
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-bold text-sm">{item.label}</p>
                      <p className="text-white/40 text-xs mt-0.5">{item.desc}</p>
                      {st.state === 'sent' && st.subject && (
                        <p className="text-white/30 text-[11px] mt-1 truncate">↳ "{st.subject}"</p>
                      )}
                      {st.state === 'failed' && st.error && (
                        <p className="text-red-400/80 text-[11px] mt-1 truncate">↳ {st.error}</p>
                      )}
                    </div>
                    <StatusBadge state={st.state} at={st.at} />
                    <button
                      onClick={() => sendOne(item.key)}
                      disabled={st.state === 'sending' || sendingAll}
                      className="border border-white/10 hover:border-primary/50 text-white hover:text-primary font-bold text-[11px] tracking-widest px-4 h-9 flex items-center gap-1.5 transition-colors disabled:opacity-40"
                    >
                      {st.state === 'sending' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                      SEND TEST
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        <div className="text-center pt-4">
          <p className="text-white/30 text-xs">
            All samples are sent via the Resend relay to <span className="text-white/50">{recipient || 'the address above'}</span> — check spam and promotions tabs.
          </p>
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ state, at }) {
  const label = { idle: 'IDLE', sending: 'SENDING', sent: 'SENT', failed: 'FAILED' }[state];
  const Icon = { idle: Clock, sending: Loader2, sent: Check, failed: X }[state];
  const time = at ? new Date(at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '';
  return (
    <span className={`hidden sm:inline-flex items-center gap-1.5 text-[10px] font-bold tracking-widest px-2.5 py-1.5 border ${statusStyles[state]}`}>
      <Icon className={`w-3 h-3 ${state === 'sending' ? 'animate-spin' : ''}`} />
      {label}
      {time && <span className="text-white/30 font-normal normal-case tracking-normal">· {time}</span>}
    </span>
  );
}